import { PropsWithChildren, useEffect, lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { Permission } from '@act/shared/authorization';
import AuthVisibilityCheck from 'react-components/auth/auth-visibility-check/AuthVisibilityCheck';
import AutoLogout from 'react-components/auth/auto-logout/AutoLogout';
import {
  actingUserAppSelectors,
} from 'redux/slices/actingUserSlice.slice';
import { ensureVerifyUser } from 'redux/thunks/referenceData.thunk';
import { useAppDispatch } from 'redux/storeHooks';
import { authService } from 'redux/utils/auth/authService';
import Spinner from 'toolkit/progress/spinner';
import { useAppLocation } from './useAppLocation';
import { useAppNavigation } from './useAppNavigation.util';
import { AppHeader, FlexContainer, IconMessage } from '@act/storybook';
import AppGrid from '../AppGrid';
import RouteLoadingFallback from './RouteLoadingFallback';
import { ENROLLMENT_MEMBER_LIST_PATH } from './routes/EnrollmentRoutes';
import { getReportsAppOrigin } from 'utilities/getReportsAppOrigin.util';

const AdminDashboardSideMenu = lazy(() =>
  import('react-components/admin-dashboard/AdminDashboardSideMenu').then(
    module => ({
      default: module.default,
    })
  )
);

const CarePortalSideMenu = lazy(() =>
  import('../CarePortal/CarePortalSideMenu').then(module => ({
    default: module.default,
  }))
);

const ErrorPage = lazy(() =>
  import('./routes/StatusPages/ErrorPage').then(module => ({
    default: module.default,
  }))
);

const ProtectedRoute = ({ children }: PropsWithChildren) => {
  const loadingStatus = actingUserAppSelectors.useSelectLoadingStatus();
  const authError = actingUserAppSelectors.useSelectActingUserError();
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const canViewMembers = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_VIEW_MEMBERS,
  );
  const canAccessAdminPortal = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_ACCESS_ADMIN_PORTAL,
  );
  const dispatch = useAppDispatch();
  const appLocation = useAppLocation();
  const { pathname } = useLocation();
  const navigate = useAppNavigation();

  const fullBleedMain =
    pathname === ENROLLMENT_MEMBER_LIST_PATH ||
    pathname.startsWith(`${ENROLLMENT_MEMBER_LIST_PATH}/`);

  const handleLogout = () => authService.logout();

  useEffect(() => {
    if (loadingStatus === 'initial') {
      dispatch(ensureVerifyUser());
    }
    if (loadingStatus === 'failed') {
      setTimeout(handleLogout, 1500);
    }
  }, [loadingStatus, dispatch]);

  // Handle access control navigation
  useEffect(() => {
    if (loadingStatus === 'success' && !actingUser?.hasAppAccess) {
      navigate.toNoAccess();
    }
  }, [loadingStatus, actingUser?.hasAppAccess, navigate]);

  useEffect(() => {
    if (
      loadingStatus === 'success' &&
      actingUser?.hasAppAccess &&
      !canViewMembers &&
      !canAccessAdminPortal
    ) {
      window.location.assign(getReportsAppOrigin());
    }
  }, [
    loadingStatus,
    actingUser?.hasAppAccess,
    canViewMembers,
    canAccessAdminPortal,
  ]);

  if (loadingStatus === 'success') {
    const sideMenuContent =
      appLocation === 'admin-dashboard' ? (
        <AdminDashboardSideMenu />
      ) : (
        <CarePortalSideMenu />
      );

    /*
     * Tools that render their own left navigation drawer opt out of the header
     * accent, which is also what drops AppShellGrid's 16px main inset so the
     * drawer sits flush against the app side menu.
     */
    const hideHeaderAccent =
      appLocation === 'admin-dashboard' ||
      appLocation === 'manager-operating-system';
    return (
      <>
        <AuthVisibilityCheck />
        <AutoLogout />
        <AppGrid
          headerContent={<AppHeader />}
          sideMenuContent={sideMenuContent}
          mainContent={children}
          isOnlyContentPage={appLocation === 'avon-patient'}
          hideHeaderAccent={hideHeaderAccent}
          fullBleedMain={fullBleedMain}
        />
      </>
    );
  } else if (loadingStatus === 'failed') {
    if (authError.code === '401') {
      return (
        <FlexContainer
          direction="row"
          justifyContent="center"
          alignItems="center"
          height="100%"
          width="100%"
        >
          <IconMessage
            message="Not logged in"
            subMessage="Redirecting you to login page"
            iconName="UserCircleDashed"
            iconColor="error"
          />
        </FlexContainer>
      );
    } else {
      return (
        <Suspense fallback={<RouteLoadingFallback />}>
          <ErrorPage />
        </Suspense>
      );
    }
  } else {
    return (
      <FlexContainer
        direction="row"
        justifyContent="center"
        alignItems="center"
        height="100%"
        width="100%"
      >
        <Spinner centered={true} />
      </FlexContainer>
    );
  }
};

export default ProtectedRoute;
